import { ReactNode, useRef } from "react";
import { Bug, Gamepad2, Maximize, Minimize, Music, Pause, PawPrint, Volume2, VolumeX, X } from "lucide-react";
import { Snapshot } from "@/game/types";
import { t } from "@/game/i18n";

const DIGIT_COLORS = ["#facc15", "#f472b6", "#38bdf8", "#4ade80", "#fb923c", "#c084fc"];
const LETTER_COLORS = ["#f472b6", "#facc15", "#4ade80", "#38bdf8", "#c084fc"];

const Rainbow = ({ value, testId }: { value: number; testId: string }) => (
  <span data-testid={testId} className="bt-digits" aria-label={String(value)}>
    {String(value).split("").map((ch, i) => (
      <span key={i} style={{ color: DIGIT_COLORS[i % DIGIT_COLORS.length] }}>{ch}</span>
    ))}
  </span>
);

export const Hud = ({ snap, actions, onSecret }: { snap: Snapshot; actions?: ReactNode; onSecret?: () => void }) => {
  const taps = useRef<number[]>([]);
  const tapLives = () => {
    const now = Date.now();
    taps.current = [...taps.current.filter((t) => now - t < 2500), now];
    if (taps.current.length >= 5) { taps.current = []; onSecret?.(); }
  };
  return (
  <div data-testid="game-hud" className="hud-top">
    <button type="button" data-testid="hud-lives-button" className="hud-item" title={t("lives")} onClick={tapLives}>
      <PawPrint size={16} className={snap.god ? "text-emerald-300 fill-emerald-300" : "text-red-400 fill-red-400"} />
      <span data-testid="hud-player-lives" className="bt-digits" style={{ color: snap.god ? "#6ee7b7" : "#facc15" }} aria-label={`${snap.lives} lives`}>{snap.god ? "∞" : snap.lives}</span>
    </button>
    {snap.god && <span data-testid="hud-god-badge" className="hud-god">GOD</span>}
    <Rainbow value={snap.score} testId="hud-current-score" />
    <span data-testid="hud-extra-letters" className="bt-digits tracking-widest" style={snap.daily ? { display: "none" } : undefined}>
      {["P", "U", "P", "P", "Y"].map((l, i) => (
        <span key={i} data-testid={`extra-letter-${i}`} className={snap.letters[i] ? "letter-on" : "letter-off"} style={snap.letters[i] ? { color: LETTER_COLORS[i] } : undefined}>{l}</span>
      ))}
    </span>
    <div className="hud-item">
      <span className="hud-label">{snap.daily ? t("wave") : snap.bonus ? t("tunnel") : t("niv")}</span>
      <span data-testid="hud-current-level" className="bt-digits" style={{ color: snap.daily ? "#7dd3fc" : snap.bonus ? "#fde047" : "#38bdf8" }}>{snap.daily ? snap.wave : snap.level}</span>
    </div>
    {snap.daily && (
      <div className="hud-item">
        <span data-testid="hud-daily-time" className="bt-digits" style={{ color: "#e2e8f0" }}>{`${Math.floor(snap.elapsed / 60000)}:${String(Math.floor(snap.elapsed / 1000) % 60).padStart(2, "0")}`}</span>
      </div>
    )}
    {!snap.daily && (
    <div className="hud-item">
      <span className="hud-label">{t("best")}</span>
      <Rainbow value={snap.best} testId="hud-high-score" />
    </div>
    )}
    <div className="hud-item" title={t("enemies")}>
      <Bug size={15} className="text-lime-300" />
      <span data-testid="enemies-left" className="bt-digits" style={{ color: "#bef264" }}>{snap.enemiesLeft}</span>
    </div>
    {actions && <div className="hud-slot">{actions}</div>}
  </div>
  );
};

interface ActionProps {
  phase: Snapshot["phase"];
  muted: boolean;
  music: boolean;
  dpad: boolean;
  fullscreen: boolean | null;
  onMute: () => void;
  onMusic: () => void;
  onDpad: () => void;
  onPause: () => void;
  onFullscreen: () => void;
  onQuit: () => void;
}

export const HudActions = ({ phase, muted, music, dpad, fullscreen, onMute, onMusic, onDpad, onPause, onFullscreen, onQuit }: ActionProps) => (
  <div data-testid="hud-actions" className="hud-actions">
    <button data-testid="toggle-dpad-button" onClick={onDpad} className={`icon-btn icon-btn-sm ${dpad ? "icon-btn-on" : ""}`} aria-label="D-pad"><Gamepad2 size={15} /></button>
    <button data-testid="music-toggle" onClick={onMusic} className={`icon-btn icon-btn-sm ${music ? "icon-btn-on" : "icon-btn-off"}`} aria-label="Music" aria-pressed={music}><Music size={15} /></button>
    <button data-testid="mute-audio-toggle" onClick={onMute} className="icon-btn icon-btn-sm" aria-label="Mute">{muted ? <VolumeX size={15} /> : <Volume2 size={15} />}</button>
    {fullscreen !== null && (
      <button data-testid="fullscreen-toggle" onClick={onFullscreen} className="icon-btn icon-btn-sm" aria-label="Fullscreen">{fullscreen ? <Minimize size={15} /> : <Maximize size={15} />}</button>
    )}
    <button data-testid="pause-game-button" onClick={onPause} className="icon-btn icon-btn-sm pause-btn" aria-label="Pause" disabled={phase !== "playing" && phase !== "paused"}><Pause size={15} /></button>
    <button data-testid="quit-game-button" onClick={onQuit} className="icon-btn icon-btn-sm icon-btn-quit" aria-label="Quit" title={t("quit")}><X size={16} /></button>
  </div>
);
